<template>
  <AdminLayout>
    <div class="max-w-4xl mx-auto">
      <!-- 헤더 -->
      <div class="flex items-center gap-3 mb-6">
        <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-800">포인트 설정</h1>
          <p class="text-sm text-slate-500">활동별 포인트 적립 금액을 설정합니다 (1P = 1원)</p>
        </div>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-slate-500">설정 불러오는 중...</p>
      </div>

      <!-- 설정 폼 -->
      <form v-else @submit.prevent="saveSettings" class="space-y-6">
        <!-- 기본 활동 -->
        <div class="bg-white rounded-2xl p-6 shadow-sm">
          <h2 class="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <span class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-sm">1</span>
            기본 활동
          </h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">🎉</span>
                회원가입
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.signup" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">최초 가입 시 1회 지급</p>
            </div>

            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">👤</span>
                프로필 완성
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.profileComplete" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">이름, 연락처 등록 시 1회 지급</p>
            </div>
          </div>
        </div>

        <!-- 출석체크 -->
        <div class="bg-white rounded-2xl p-6 shadow-sm">
          <h2 class="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <span class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-sm">2</span>
            출석체크
          </h2>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">📅</span>
                일일 출석
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.dailyCheck" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">매일 출석체크 시</p>
            </div>

            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">🔥</span>
                7일 연속 보너스
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.streak7" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">7일 연속 출석 시 추가 지급</p>
            </div>

            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">🏆</span>
                30일 연속 보너스
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.streak30" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">30일 연속 출석 시 추가 지급</p>
            </div>
          </div>
        </div>

        <!-- 핵심 활동 -->
        <div class="bg-white rounded-2xl p-6 shadow-sm">
          <h2 class="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <span class="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center text-sm">3</span>
            핵심 활동
          </h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="setting-item highlight">
              <label class="setting-label">
                <span class="setting-icon">💬</span>
                상담 신청
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.consultation" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">상담 신청 완료 시</p>
            </div>

            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">📍</span>
                현장 방문 인증
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.visitVerify" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">Admin 승인 후 지급</p>
            </div>

            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">📝</span>
                계약 완료
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.contractComplete" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">계약 확인 후 지급</p>
            </div>

            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">⭐</span>
                앱 리뷰 작성
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.appReview" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">스토어 리뷰 인증 시 1회</p>
            </div>
          </div>
        </div>

        <!-- 공유 & 초대 -->
        <div class="bg-white rounded-2xl p-6 shadow-sm">
          <h2 class="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <span class="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center text-sm">4</span>
            공유 & 초대
          </h2>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">📤</span>
                SNS 공유
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.snsShare" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">현장 공유 시 (일 1회)</p>
            </div>

            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">👥</span>
                친구 초대 (추천인)
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.referrer" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">초대한 친구가 가입 시</p>
            </div>

            <div class="setting-item">
              <label class="setting-label">
                <span class="setting-icon">🎁</span>
                피초대자 보너스
              </label>
              <div class="setting-input-wrap">
                <input v-model.number="settings.referee" type="number" min="0" class="setting-input" />
                <span class="setting-unit">P</span>
              </div>
              <p class="setting-desc">초대받고 가입한 사람</p>
            </div>
          </div>
        </div>

        <!-- 저장 버튼 -->
        <div class="flex justify-end gap-3">
          <button
            type="button"
            @click="resetToDefault"
            class="px-6 py-3 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-50 transition"
          >
            기본값으로 초기화
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
          >
            {{ saving ? '저장 중...' : '설정 저장' }}
          </button>
        </div>
      </form>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, onMounted } from 'vue'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { usePoint } from '@/composables/main/usePoint'

export default {
  name: 'PointSettingsView',
  components: {
    AdminLayout
  },
  setup() {
    const { getPointSettings, updatePointSettings, DEFAULT_SETTINGS } = usePoint()

    const loading = ref(true)
    const saving = ref(false)
    const settings = ref({ ...DEFAULT_SETTINGS })

    const loadSettings = async () => {
      loading.value = true
      try {
        const data = await getPointSettings()
        settings.value = { ...DEFAULT_SETTINGS, ...data }
      } catch (err) {
        console.error('설정 로드 실패:', err)
      } finally {
        loading.value = false
      }
    }

    const saveSettings = async () => {
      saving.value = true
      try {
        await updatePointSettings(settings.value)
        alert('설정이 저장되었습니다.')
      } catch (err) {
        alert('저장에 실패했습니다: ' + err.message)
      } finally {
        saving.value = false
      }
    }

    const resetToDefault = () => {
      if (confirm('기본값으로 초기화하시겠습니까?')) {
        settings.value = { ...DEFAULT_SETTINGS }
      }
    }

    onMounted(loadSettings)

    return {
      loading,
      saving,
      settings,
      saveSettings,
      resetToDefault
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.setting-item {
  @apply p-4 bg-slate-50 rounded-xl;
}

.setting-item.highlight {
  @apply bg-violet-50 border-2 border-violet-200;
}

.setting-label {
  @apply flex items-center gap-2 text-sm font-medium text-slate-700 mb-2;
}

.setting-icon {
  @apply text-lg;
}

.setting-input-wrap {
  @apply flex items-center gap-2;
}

.setting-input {
  @apply flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-right font-semibold text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500;
}

.setting-unit {
  @apply text-slate-500 font-medium;
}

.setting-desc {
  @apply text-xs text-slate-400 mt-2;
}
</style>
